/**
 * Type surface for the internal Issue Reporter — a lightweight defect
 * tracker (NOT the customer support-ticket system in ticket-service).
 * Anyone in the console can raise an issue with the Ctrl/Cmd+Shift+B
 * hotkey; the tech team triages + fixes; the reporter approves the
 * fix or rejects for another round.
 *
 * <p>FE-only mock for now — mirrors the design bundle's data-issues.jsx
 * one-for-one so a future backend can pick up the same shapes.
 */

export type IssueSeverityId = 'Blocker' | 'High' | 'Medium' | 'Low';
export type IssueStatusId   = 'New' | 'Triaged' | 'In Progress' | 'Fixed' | 'Approved' | 'Parked'
  | 'Needs Reporter';
/** Which side of the round-robin currently owes a move. */
export type IssueOwner      = 'tech' | 'reporter' | 'done';

export interface IssueSeverity {
  id: IssueSeverityId;
  /** Extra CSS-class token (kept lowercase, distinct from {@link id}) so
   *  templates can compose {@code .iss-sev.blocker} etc. */
  kind: 'blocker' | 'high' | 'medium' | 'low';
  weight: number;
  label: string;
  hint: string;
}

export interface IssueState {
  id: IssueStatusId;
  kind: 'info' | 'accent' | 'warning' | 'success' | 'muted';
  /** 0-indexed step position on the 5-node track. */
  step: number;
  owner: IssueOwner;
  blurb: string;
}

export interface IssuePerson {
  id: string;
  name: string;
  initials: string;
  color: string;
  role: string;
  /** True for tech-team members (candidates for the Assignee dropdown). */
  tech?: boolean;
}

export interface IssueAnnotationBox {
  type: 'box';
  x: number; y: number; w: number; h: number;
}
export interface IssueAnnotationArrow {
  type: 'arrow';
  x1: number; y1: number; x2: number; y2: number;
}
export type IssueAnnotation = IssueAnnotationBox | IssueAnnotationArrow;

export interface IssueLocation {
  route: string;
  crumbs: string[];
  hash: string;
  module: string;
}

export interface IssueEnv {
  viewport: string;
  theme: string;
  browser: string;
}

/** Frozen page capture — a PNG data URL plus the capture viewport size.
 *  Kept small (html-to-image at default settings), rendered back in the
 *  snapshot pane scaled to fit. */
export interface IssueSnapshot {
  /** {@code data:image/png;base64,...} */
  dataUrl: string;
  w: number;
  h: number;
  scrollY: number;
}

export interface IssueEvent {
  t: Date;
  label: string;
  by: string;
}

/** One chat entry in the issue thread. {@code side} drives which bubble
 *  side we render on; {@code system} is a centered neutral note. */
export interface IssueMessage {
  id: string;
  side: 'reporter' | 'tech' | 'system';
  author?: string;
  /** Denormalised author display name from the backend — preferred over
   *  resolving {@link author} against the directory / mock so the reporter
   *  side shows a name, not a raw identity id. */
  authorName?: string;
  text: string;
  at: Date;
}

export interface Issue {
  id: string;
  title: string;
  description: string;
  severity: IssueSeverityId;
  status: IssueStatusId;
  /** How many verification rounds have already happened. Reject-at-Fixed
   *  bumps this by one. */
  rounds: number;
  reporterId: string;
  assignee: string | null;
  /** Denormalised assignee display name from the backend (the name picked from
   *  the issue-desk directory). Preferred over resolving {@link assignee} against
   *  the directory / mock so the reporter side renders the owner too. */
  assigneeName?: string;
  location: IssueLocation;
  env: IssueEnv;
  createdAt: Date;
  updatedAt: Date;
  snapshot: IssueSnapshot | null;
  annotations: IssueAnnotation[];
  events: IssueEvent[];
  messages: IssueMessage[];
}

// ── static catalogues ────────────────────────────────────────────────

export const ISSUE_SEVERITIES: IssueSeverity[] = [
  { id: 'Blocker', kind: 'blocker', weight: 4, label: 'Blocker', hint: 'Work is completely stopped — nothing can proceed.' },
  { id: 'High',    kind: 'high',    weight: 3, label: 'High',    hint: 'A major feature is broken with no reasonable workaround.' },
  { id: 'Medium',  kind: 'medium',  weight: 2, label: 'Medium',  hint: "Something's wrong but there is a workaround." },
  { id: 'Low',     kind: 'low',     weight: 1, label: 'Low',     hint: 'Cosmetic or a minor annoyance.' },
];

export const ISSUE_STATES: IssueState[] = [
  { id: 'New',         kind: 'info',    step: 0, owner: 'tech',     blurb: 'Reported — waiting for the tech team to triage.' },
  { id: 'Triaged',     kind: 'accent',  step: 1, owner: 'tech',     blurb: 'Acknowledged and assigned to an engineer.' },
  { id: 'In Progress', kind: 'warning', step: 2, owner: 'tech',     blurb: 'An engineer is working on a fix.' },
  { id: 'Fixed',       kind: 'accent',  step: 3, owner: 'reporter', blurb: 'Fix shipped — waiting for the reporter to verify.' },
  { id: 'Approved',    kind: 'success', step: 4, owner: 'done',     blurb: 'Reporter confirmed the fix. Issue closed.' },
  // step -1 = OFF THE TRACK, matching IssueStatus.OFF_TRACK on the server. Parking can happen at
  // any point and resume to any point, so it has no position in the 5-node progress line; giving
  // it 5 would draw it after Approved, which reads as "past done".
  { id: 'Parked',      kind: 'muted',   step: -1, owner: 'tech',     blurb: 'On hold — work paused, not abandoned. Resume to any status.' },
  // Also off-track (-1): a question can block any step. owner 'reporter' is the load-bearing bit —
  // it is what tells the UI the ball is in the reporter's court, as it already does for Fixed.
  { id: 'Needs Reporter', kind: 'warning', step: -1, owner: 'reporter', blurb: 'Blocked on you — answer in the thread so work can continue.' },
];

/** The five linear states, in order — what the progress track renders. Excludes off-track ones. */
export const ISSUE_TRACK_STATES: IssueState[] = ISSUE_STATES.filter((s) => s.step >= 0);

/** True for states with no position on the progress track (currently just Parked). */
export const issueOffTrack = (id: IssueStatusId): boolean => issueState(id).step < 0;

// Parked counts as OPEN: the work is paused, not finished. Leaving it out would quietly drop
// parked issues from every open-issue count and filter, which is how parked work gets forgotten.
const ISSUE_OPEN: IssueStatusId[] = ['New', 'Triaged', 'In Progress', 'Fixed', 'Parked',
  'Needs Reporter'];
export const issueIsOpen = (s: IssueStatusId): boolean => ISSUE_OPEN.includes(s);

export function issueSev(id: IssueSeverityId): IssueSeverity {
  return ISSUE_SEVERITIES.find((s) => s.id === id) ?? ISSUE_SEVERITIES[2];
}
export function issueState(id: IssueStatusId): IssueState {
  return ISSUE_STATES.find((s) => s.id === id) ?? ISSUE_STATES[0];
}

/** Mock tech team — Assignee dropdown pulls from here in v1. */
export const ISSUE_TEAM: Record<string, IssuePerson> = {
  ava:    { id: 'ava',    name: 'Ava Doyle',    initials: 'AD', color: '#DD5A2E', role: 'Platform Admin',    tech: true },
  nadia:  { id: 'nadia',  name: 'Nadia Cole',   initials: 'NC', color: '#2C5DAA', role: 'Frontend Engineer', tech: true },
  marcus: { id: 'marcus', name: 'Marcus Reeve', initials: 'MR', color: '#137A56', role: 'Backend Engineer',  tech: true },
  priya:  { id: 'priya',  name: 'Priya Nair',   initials: 'PN', color: '#6B4DE6', role: 'QA Lead',           tech: true },
};

/** Mock reporters (staff / agents who raise issues). */
export const ISSUE_PEOPLE: Record<string, IssuePerson> = {
  ahmed: { id: 'ahmed', name: 'Ahmed Hassan',   initials: 'AH', color: '#B57A14', role: 'Falcon Travel · Agent' },
  sara:  { id: 'sara',  name: 'Sara Kim',       initials: 'SK', color: '#B33A3A', role: 'Globe Trotters · Agent' },
  leila: { id: 'leila', name: 'Leila Farahani', initials: 'LF', color: '#0E8A74', role: 'Reservations Desk' },
  omid:  { id: 'omid',  name: 'Omid Rezaei',    initials: 'OR', color: '#8A5A1B', role: 'Finance Team' },
};

export function issuePerson(id: string | null | undefined): IssuePerson | null {
  if (!id) return null;
  return ISSUE_TEAM[id] ?? ISSUE_PEOPLE[id] ?? null;
}

/** Current signed-in identity used by the mock — mirrors the JSX
 *  {@code issMe(role)}. Real wiring should replace this with the
 *  keycloak-password profile once we bind the store to identity. */
export function issueMe(role: 'admin' | 'agent'): IssuePerson {
  return role === 'admin' ? ISSUE_TEAM['ava'] : ISSUE_PEOPLE['ahmed'];
}

/** {@code Date → "12 Aug 2026", "14:52"} — matches the design's issFmt. */
export function issueFmt(d: Date | string): { greg: string; time: string } {
  const date = d instanceof Date ? d : new Date(d);
  return {
    greg: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
  };
}

/** "just now" / "12m ago" / "3h ago" / "2d ago" / date string. */
export function issueRelative(d: Date | string): string {
  const date = d instanceof Date ? d : new Date(d);
  const mins = Math.round((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  if (days < 7) return `${days}d ago`;
  return issueFmt(date).greg;
}
